import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';

import { SEARCH_PARAMS } from 'components/search/InstantSearch/types';
import type { AvailableLanguage } from 'i18n/useTranslations';
import {
  APPEARANCES_15_MAX,
  APPEARANCES_5_MAX,
  APPEARANCES_MIN,
} from 'lib/cards';
import {
  cardAttributes,
  fantasy,
  filters,
  glossary,
  navLabels,
} from 'lib/glossary';
import { attributes as playerAttributes } from 'lib/players';

type RealFilterWidget = {
  attribute: string;
  type: 'list' | 'range' | 'toggle' | 'menu';
};

type SortFilterWidget = {
  type: 'sort';
};

type VirtualToggleFilterWidget = {
  filter: VirtualToggleFilter;
  type: 'virtualToggle' | 'raw';
};

export type FilterSeparator = {
  type: 'separator';
};

export type FilterWidget<
  T extends { visible: boolean } = { visible: boolean },
> = (RealFilterWidget | SortFilterWidget | VirtualToggleFilterWidget) & {
  key: string;
  title?: ReactNode;
  shortTitle?: ReactNode;
  Component: (props: T) => ReactNode;
  props?: T;
  accordionOptions?: {
    startsOpen: boolean;
  };
};

export type Filter = {
  attribute: string;
  urlAttribute?: string;
  i18nAttribute?: (language: AvailableLanguage) => string;
  title: MessageDescriptor;
  shortTitle?: MessageDescriptor;
  label?: MessageDescriptor;
  alias?: string;
  min?: number;
  max?: number;
};

export type VirtualToggleFilter = {
  name: string;
  trackingName: string;
  title?: MessageDescriptor;
  label?: MessageDescriptor;
  defaultValue?: boolean | string[];
  skipClear?: boolean;
  min?: number;
  max?: number;
};

export type ToggleFilter = {
  attribute: string;
  label: MessageDescriptor | string;
};

export type FiltersGroup = {
  title?: ReactNode;
  filters: (FilterWidget | FilterSeparator)[];
  // allow showing a single filter without a border
  filter?: FilterWidget;
  slug: string;
};

export const FLOOR_PRICES_FILTERS = {
  inSeasonLimited: {
    attribute: 'floor_prices.in_season.limited',
    urlAttribute: 'fp-is-l',
    title: filters.floorPrices,
  },
  inSeasonRare: {
    attribute: 'floor_prices.in_season.rare',
    urlAttribute: 'fp-is-r',
    title: filters.floorPrices,
  },
  inSeasonSuperRare: {
    attribute: 'floor_prices.in_season.super_rare',
    urlAttribute: 'fp-is-sr',
    title: filters.floorPrices,
  },
  inSeasonUnique: {
    attribute: 'floor_prices.in_season.unique',
    urlAttribute: 'fp-is-u',
    title: filters.floorPrices,
  },
  allSeasonsLimited: {
    attribute: 'floor_prices.all_seasons.limited',
    urlAttribute: 'fp-as-l',
    title: filters.floorPrices,
  },
  allSeasonsRare: {
    attribute: 'floor_prices.all_seasons.rare',
    urlAttribute: 'fp-as-r',
    title: filters.floorPrices,
  },
  allSeasonsSuperRare: {
    attribute: 'floor_prices.all_seasons.super_rare',
    urlAttribute: 'fp-as-sr',
    title: filters.floorPrices,
  },
  allSeasonsUnique: {
    attribute: 'floor_prices.all_seasons.unique',
    urlAttribute: 'fp-as-u',
    title: filters.floorPrices,
  },
} satisfies { [key: string]: Filter };

export const FILTERS = {
  rarity: {
    attribute: 'rarity',
    title: cardAttributes.scarcity,
  },
  settlementWallets: {
    attribute: 'sale.settlement_wallets',
    title: filters.settlementWallets,
  },
  position: {
    attribute: 'position',
    urlAttribute: 'pos',
    title: cardAttributes.position,
  },
  team: {
    attribute: 'team.long_name',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    i18nAttribute: language => `team.long_name_with_slug`,
    title: cardAttributes.team,
  },
  activeClub: {
    attribute: 'active_club.long_name',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    i18nAttribute: language => `active_club.long_name_with_slug`,
    title: filters.activeClub,
  },
  activeTeam: {
    attribute: 'active_team.long_name',
    title: filters.activeTeam,
  },
  activeNationalTeam: {
    attribute: 'active_national_team.long_name',
    i18nAttribute: language => `active_national_team.name_${language}`,
    title: filters.activeNationalTeam,
  },
  nationality: {
    attribute: 'country.code',
    i18nAttribute: language => `country.name_with_code_${language}`,
    title: filters.nationality,
  },
  birthplace: {
    attribute: 'country.code',
    i18nAttribute: language => `country.name_with_code_${language}`,
    title: filters.birthplace,
  },
  player: {
    attribute: 'player.display_name',
    title: glossary.name,
  },
  season: {
    attribute: 'season',
    title: cardAttributes.season,
  },
  activeLeague: {
    attribute: 'active_leagues.display_name',
    i18nAttribute: language => `active_leagues.name_with_slug_${language}`,
    title: filters.tournament,
  },
  cardEdition: {
    attribute: 'card_edition.display_name',
    title: cardAttributes.cardEdition,
  },
  bundledSale: {
    attribute: 'sale.bundled',
    title: filters.bundledSale,
  },
  saleType: {
    attribute: 'sale.type',
    title: filters.saleType,
  },
  collectible: {
    attribute: 'rarity',
    title: filters.nonPlayableCardsFilterLabel,
    alias: 'co',
  },
  customDeck: {
    attribute: 'custom_decks.name',
    title: filters.customDeck,
  },
  customList: {
    attribute: 'custom_decks.name',
    title: filters.customList,
  },
  cardLevel: {
    attribute: 'grade',
    title: filters.cardLevel,
  },
  averageScore: {
    attribute: 'average_score',
    title: filters.averageScore,
  },
  lastFiveAverageScore: {
    attribute: 'so5.last_five_so5_average_score',
    title: filters.lastFiveAverageScore,
    alias: 'L5',
  },
  lastFifteenAverageScore: {
    attribute: 'so5.last_fifteen_so5_average_score',
    title: filters.lastFifteenAverageScore,
    alias: 'L15',
  },
  appearances: {
    attribute: 'appearances',
    title: filters.appearances,
  },
  lastFiveAppearances: {
    attribute: 'so5.last_five_so5_appearances',
    title: filters.lastFiveAppearances,
    min: APPEARANCES_MIN,
    max: APPEARANCES_5_MAX,
  },
  lastFifteenAppearances: {
    attribute: 'so5.last_fifteen_so5_appearances',
    title: filters.lastFifteenAppearances,
    min: APPEARANCES_MIN,
    max: APPEARANCES_15_MAX,
  },
  price: {
    attribute: 'sale.price',
    title: filters.price,
  },
  serialNumber: {
    attribute: 'serial_number',
    title: cardAttributes.serialNumber,
  },
  age: {
    attribute: 'player.birth_date_i',
    title: playerAttributes.age,
  },
  nbaTenGameAverageScore: {
    attribute: 'so5.last_ten_played_so5_average_score',
    title: filters.nbaTenGameAverageScore,
    shortTitle: filters.nbaShortTenGameAverageScore,
  },
  mlbLastFifteenAverageScore: {
    attribute: 'so5.last_fifteen_so5_average_score',
    title: filters.mlbLastFifteenAverageScore,
  },
  mlbSeasonAverageScore: {
    attribute: 'so5.season_average_score',
    title: filters.mlbSeasonAverageScore,
  },
  mlbEligibleLeaderboards: {
    attribute: 'eligible_leaderboards',
    title: filters.tournament,
  },
  playingStatus: {
    attribute: 'player.playing_status',
    urlAttribute: 'ps',
    title: filters.playingStatusFilterLabel,
  },
  projectedScoreClassic: {
    attribute: 'next_classic_fixture_projected_score',
    title: filters.projectedScoreClassic,
  },
  projectedScoreDifferenceClassic: {
    attribute: 'difference_last_ten_and_classic_projection',
    title: filters.projectedScoreDifferenceClassic,
  },
  projectedScoreDaily: {
    attribute: 'next_daily_fixture_projected_score',
    title: filters.projectedScoreDaily,
  },
  projectedScoreDifferenceDaily: {
    attribute: 'difference_last_ten_and_daily_projection',
    title: filters.projectedScoreDifferenceDaily,
  },
  ...FLOOR_PRICES_FILTERS,
} as const satisfies { [key: string]: Filter };

export const VIRTUAL_TOGGLE_FILTERS = {
  gameweekFilter: {
    name: 'gw',
    trackingName: 'teamHasGame',
    title: filters.gameweekFilterLabel,
    shortTitle: navLabels.schedule,
    defaultValue: false,
  },
  dailyFilter: {
    name: 'dly',
    trackingName: 'dailyFilter',
    title: filters.dailyFilterLabel,
    shortTitle: navLabels.schedule,
    defaultValue: false,
  },
  probablePitchersFilter: {
    name: 'pp',
    trackingName: 'probablePitchers',
    title: filters.probablePitchersFilterLabel,
    defaultValue: false,
  },
  favoriteFilter: {
    name: 'ff',
    trackingName: 'favorite',
    label: filters.favoriteFilterLabel,
    defaultValue: false,
  },
  competitionEligibilityFilter: {
    name: 'lf',
    trackingName: 'league',
    title: filters.competitionEligibilityFilterTitle,
    defaultValue: false,
  },
  customDecksFilter: {
    name: 'deck',
    trackingName: 'customDecksFilter',
    title: filters.customList,
    defaultValue: false,
  },
  notInLineupFilter: {
    name: 'nl',
    trackingName: 'notInLineup',
    label: filters.notInLineupFilterLabel,
    defaultValue: false,
  },
  unstackedFilter: {
    name: 'unstacked',
    trackingName: 'unstacked',
    defaultValue: false,
    skipClear: true,
  },
  promotionalEventFilter: {
    name: SEARCH_PARAMS.PROMOTIONAL_EVENT,
    trackingName: 'promotionalEvent',
    title: filters.promotionalEvent,
    defaultValue: false,
  },
  statsModeFilter: {
    name: SEARCH_PARAMS.STATS_MODE,
    trackingName: 'statsMode',
    label: cardAttributes.position,
    defaultValue: false,
  },
  watchlistFilter: {
    name: SEARCH_PARAMS.WATCHLIST,
    trackingName: 'watchlist',
    label: filters.watchlist,
    defaultValue: false,
  },
  duplicateFilter: {
    name: SEARCH_PARAMS.DUPLICATED_KEY,
    trackingName: 'duplicatedKey',
    label: filters.duplicate,
    defaultValue: false,
  },
} as const;

export type VirtualToggleName =
  (typeof VIRTUAL_TOGGLE_FILTERS)[keyof typeof VIRTUAL_TOGGLE_FILTERS]['name'];

export const TOGGLE_FILTERS = {
  showDetails: {
    attribute: 'showDetails',
    label: filters.showDetailsLabel,
  },
  onSaleFilter: {
    attribute: 'on_sale',
    label: filters.onSaleFilterLabel,
  },
  creditEligibleFilter: {
    attribute: 'sale.type',
    label: filters.creditEligibleFilter,
  },
  bidToEarnFilter: {
    attribute: 'sale.bid_to_earn',
    label: filters.bidToEarnLabel,
  },
  inSeasonFilter: {
    attribute: 'in_season_eligible',
    label: 'In Season',
  },
  jerseySerialFilter: {
    attribute: 'jersey_serial',
    label: filters.jerseySerialLabel,
  },
} as const satisfies { [key: string]: ToggleFilter };

export const MENU = {
  club: {
    attribute: 'active_club.long_name_with_slug',
    title: filters.activeClub,
  },
  position: {
    attribute: 'position',
    title: cardAttributes.position,
  },
  revealableCardCampaign: {
    type: 'menu',
    attribute: 'revealable_card_campaign.name',
    title: fantasy.revealCards,
  },
  rarity: {
    type: 'menu',
    attribute: 'rarity',
    title: cardAttributes.scarcity,
  },
} as const;
